import { axiosInstance } from "../../../api/axiosInstance.tsx";
import axios from 'axios';

export interface employeeSearchParams {
  name?: string;
  nik?: string;
  position?: string;
  jobTitle?: string;
  division?: string;
  gender?: string;
  status?: string;
  employeeType?: string;
  maritalStatus?: string;
  office?: string;
  company?: string;
  dateOfJoining?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

export interface payrollSearchParams {
  periodMonth?: number;
  periodYear?: number;
  paymentStatus?: string;
  paymentDate?: string;
  paymentType?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

// Define the interface at the top of the file
interface RegisterUserParams {
  email: string;
  username: string;
  password: string;
}

interface UserRegistrationResponse {
  id: string;
  username: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

interface RoleAssignmentParams {
  roleName: string;
}

interface Employee {
  id: string;
  name: string;
  nik: string;
  position: string;
  division: string;
  office: string;
  status: string;
  employeeType: string;
  userId: string; // Tambahkan ini
  email: string; // Tambahkan ini jika belum ada
}

interface UserInfoResponse {
  id: string;
  email: string;
  roles: string[];
  // tambahkan field lain sesuai response API
}

export const fetchEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await axiosInstance.get("/employees/view_employees", {
      params: {
        fields: "id,name,email,position,division,office,roles", // Hanya ambil field yang diperlukan
      },
    });

    return response.data.map((emp: any) => ({
      id: emp.id,
      name: emp.name,
      email: emp.email,
      position: emp.position,
      division: emp.division,
      office: emp.office,
    }));
  } catch (error) {
    throw new Error("Gagal mengambil data karyawan");
  }
};

// Helper function untuk format tampilan position
export const formatPositionDisplay = (position: string | string[]) => {
  try {
    // Jika position adalah string JSON, parse dulu
    const parsedPosition =
      typeof position === "string" ? JSON.parse(position) : position;

    // Jika hasilnya array, gabungkan dengan koma
    return Array.isArray(parsedPosition) ? parsedPosition.join(", ") : position;
  } catch {
    // Jika parsing gagal, kembalikan as-is
    return position;
  }
};

export const fetchInorganicEmployee = async (
  params: employeeSearchParams = {}
) => {
  const response = await axiosInstance.get("/employees/search_employees", {
    params
  });

  // Format position data di response
  const formattedData = response.data.data.map((employee) => ({
    ...employee,
    position: formatPositionDisplay(employee.position),
  }));

  return {
    ...response.data,
    data: formattedData,
  };
};

// Step 1: Create Employee
export const createPayroll = async (data) => {
  const payload = {
    employeeId: data.employeeId,
    periodMonth: data.periodMonth,
    periodYear: data.periodYear,
    paymentStatus: data.paymentStatus,
    paymentDate: data.paymentDate,
    paymentType: data.paymentType,
    thrInstallment: data.thrInstallment,
    pensionDplkInstallment: data.pensionDplkInstallment,
    bpjsTkContributionCoeff: data.bpjsTkContributionCoeff,
    bpjsTkContribution: data.bpjsTkContribution,
    bpjsKesEmployee: data.bpjsKesEmployee,
    bpjsTkEmployee: data.bpjsTkEmployee,
    jkk: data.jkk,
    jkm: data.jkm,
    pph21: data.payrollPph21,
    TER: data.tarifTER,
    gross: data.grossPayroll,
    bonus: data.bonus,
    thr: data.thr,
    kompensasi: data.kompensasi,
    others: data.others,
    overtimePay: data.overtime,
    sppd: data.sppd,
    bpjsKesCompany: data.bpjsKesCompany,
    takeHomePay: data.takeHomePay,
    bonusCoeff: data.bonusCoeff,
  };
  const response = await axiosInstance.post(
    `building/payroll-tad/create_payroll_tad`,
    payload
  );
  return response.data; // contains the item `id` or `code`
};

// Step 2: Upload Photo by Item ID
export const uploadItemPhoto = async (payrollId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post(
    `/building/payroll-tad/upload_evidence/${payrollId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data; // Should contain the URL of the uploaded image
};

export const fetchPayrollPhotoBlob = async (
  filename: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(
      `/building/payroll-tad/evidence/${filename}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    console.log(response.data);
    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto:", error);
    return null;
  }
};

// Delete employee
export const deletePayroll = async (id: string) => {
  try {
    await axiosInstance.delete(
      `/building/payroll-tad/delete_payroll_tad/${id}`
    );
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat menghapus Payroll"
    );
  }
};

// Update Employee
export const updatePayroll = async (id, data) => {
  const payloadEdit = {
    employeeId: data.employeeId,
    periodMonth: data.periodMonth,
    periodYear: data.periodYear,
    paymentStatus: data.paymentStatus,
    paymentEvidence: data.paymentEvidence,
    paymentDate: data.paymentDate,
    paymentType: data.paymentType,
    thrInstallmentCoeff: data.thrInstallmentCoeff,
    thrInstallment: data.thrInstallment,
    pensionDplkInstallmentCoeff: data.pensionDplkInstallmentCoeff,
    pensionDplkInstallment: data.pensionDplkInstallment,
    bpjsTkContributionCoeff: data.bpjsTkContributionCoeff,
    bpjsTkContribution: data.bpjsTkContribution,
    bpjsKesEmployee: data.bpjsKesEmployee,
    bpjsTkEmployee: data.bpjsTkEmployee,
    jkk: data.jkk,
    jkm: data.jkm,
    pph21: data.payrollPph21,
    TER: data.tarifTER,
    gross: data.grossPayroll,
    bonus: data.bonus,
    thr: data.thr,
    kompensasi: data.kompensasi,
    others: data.others,
    overtimePay: data.overtime,
    sppd: data.sppd,
    bpjsKesCompany: data.bpjsKesCompany,
    takeHomePay: data.takeHomePay,
    bonusCoeff: data.bonusCoeff
  };
  const response = await axiosInstance.put(
    `/building/payroll-tad/update_payroll_tad/${id}`,
    payloadEdit
  );
  return response.data;
};

export const getEmployees = async (params: employeeSearchParams = {}) => {
  const response = await axiosInstance.get("/building/payroll-tad/search_payroll_tad", {
    params,
  });

  // Format position data di response
  const formattedData = response.data.data.map((employee) => ({
    ...employee,
    position: formatPositionDisplay(employee.position),
  }));

  return {
    ...response.data,
    data: formattedData,
  };
};

// Fetch Employee Details by ID
export const getPayrollById = async (id) => {
  try {
    const response = await axiosInstance.get(
      `/building/payroll-tad/view_payroll_tad/${id}`
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

// Fetch Employee Details by ID
export const getEmployeeById = async (id) => {
  try {
    const response = await axiosInstance.get(`/employees/view_employee/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

export const fetchEmployeePhotoBlob = async (
  filename: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(`/employees/photo/${filename}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log(response.data);
    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto:", error);
    return null;
  }
};

// Fungsi untuk mencari karyawan berdasarkan keyword
export const sesarchEmployees = async (searchQuery: string) => {
  try {
    const response = await axiosInstance.get(
      `/employees/search_employees?keyword=${searchQuery}`
    );
    return response.data; // Pastikan response sesuai format yang diterima tabel
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

export const getUserInfoByEmail = async (email: string) => {
  try {
    const response = await axiosInstance.get("/admin/user-info", {
      params: { email },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to get user info";
  }
};

export const fetchRoles = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/rbac/roles");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Gagal mengambil data roles"
    );
  }
};

export const fetchVendors = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/vendors/view_vendors");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data vendor. Silakan coba lagi."
    );
  }
};

export const fetchOffices = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/offices/view_offices");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data office. Silakan coba lagi."
    );
  }
};

export const fetchPayroll = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get(
      "/building/payroll-tad/view_payroll_tad"
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data payroll. Silakan coba lagi."
    );
  }
};

export const searchPayroll = async (searchQuery: string) => {
  const response = await axiosInstance.get(
    `/building/payroll-tad/search_payroll_tad?keyword=${searchQuery}`
  );
  return response.data; // Pastikan response sesuai format yang diterima tabel
};

export const uploadItemFile = async (payrollId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post(
    `/building/payroll-tad/upload_evidence/${payrollId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export const fetchPayrollFile = async (
  filename: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(
      `/building/payroll-tad/evidence/${filename}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Untuk file PDF, kita akan membuat URL untuk blob
    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil file:", error);
    return null;
  }
};

export const bulkCreatePayroll = async (payload: {
  office: string;
  employeeStatus: string;
  paymentType: string;
  periodMonth: number;
  periodYear: number;
  paymentStatus: string;
  paymentDate: string;
  bpjsTkContributionCoeff?: number;
  bonusCoeff?: number;
}) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token tidak ditemukan');
    }

    console.log('📤 Sending bulk payload:', payload);

    const response = await axiosInstance.post(
      `/building/payroll-tad/bulk_upsert_payroll_tad`,
      payload, // ← Langsung kirim payload sesuai schema
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      }
    );

    console.log('✅ Bulk API Response:', response.data);
    return response.data;

  } catch (error) {
    console.error('❌ Bulk Create API Error:', error);
    
    // Handle error dengan lebih detail
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - Server terlalu lama merespon');
      }
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 
                            error.response.data?.error ||
                            `HTTP Error: ${error.response.status}`;
        throw new Error(errorMessage);
      } else if (error.request) {
        // Request made but no response
        throw new Error('Tidak ada response dari server');
      }
    }
    
    throw error;
  }
};

export const getTarifTER = async (nilai: number, jenisTer: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(
      `/tarif-ter/find?nilai=${nilai}&jenisTer=${jenisTer}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Gagal mengambil data tarif TER"
    );
  }
};

// Versi alternatif jika hanya mengirim file tanpa parameter tambahan
export const bulkUpsertPayrollExcel = async (file: File): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(
      '/building/payroll-tad/bulk_upsert_payroll_tad_excel',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ Bulk Upsert Excel Error:', error);
    
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const errorMessage = error.response.data?.message || 
                            error.response.data?.error ||
                            `HTTP Error: ${error.response.status}`;
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error('Tidak ada response dari server');
      }
    }
    
    throw new Error('Terjadi kesalahan saat mengupload file Excel');
  }
};
